// utils/onboardingDataTransformer.ts
import {
  UpdateGfStoreDto,
  UpdateGfMerchantDto,
  GfStoreGoogleApi,
  OpeningHourDto,
} from "@swagger/typescript-fetch-goflyer";

/**
 * Standardized onboarding data structure
 * This ensures consistent data format throughout all onboarding steps
 */
export interface StandardizedOnboardingData {
  // Store information
  store: UpdateGfStoreDto;

  // Merchant information (extracted from store)
  merchant: UpdateGfMerchantDto;

  // Additional metadata for UI
  metadata: {
    isFromGoogleApi: boolean;
    hasGooglePlaceDetails: boolean;
    googlePlaceId?: string;
    originalGoogleData?: any;
  };

  // Subscription plan information
  subscription?: {
    planId: "free" | "paid";
    planName: string;
  };
}

/**
 * Normalized opening hours format
 * Ensures consistent format for opening hours across all steps
 */
export interface NormalizedOpeningHours {
  day: number; // 0-6 (Sunday to Saturday)
  openTime: string; // Format: "HHMM" (e.g., "0900")
  closeTime: string; // Format: "HHMM" (e.g., "1700")
  isClosed: boolean; // True if the store is closed on this day
}

/**
 * Transform Google API response to standardized onboarding data
 */
export const transformGoogleApiResponse = (
  apiResponse: GfStoreGoogleApi
): StandardizedOnboardingData => {
  const storeInfo = apiResponse.storeInfo;
  const placeDetails = apiResponse.placeDetails;
  // Normalize opening hours
  const normalizedHours = normalizeOpeningHours(storeInfo.openingHours);

  // Extract individual time fields for backward compatibility
  const individualTimes =
    extractIndividualTimesFromOpeningHours(normalizedHours);

  // Create standardized store data
  const standardizedStore: UpdateGfStoreDto = {
    ...storeInfo,
    openingHours: normalizedHours,
    openTime: individualTimes.openTime,
    closeTime: individualTimes.closeTime,
    saturdayOpenTime: individualTimes.saturdayOpenTime,
    saturdayCloseTime: individualTimes.saturdayCloseTime,
    sundayOpenTime: individualTimes.sundayOpenTime,
    sundayClosedTime: individualTimes.sundayClosedTime,
    location:
      storeInfo.location && storeInfo.location.coordinates
        ? (() => {
            const normalizedCoords = normalizeCoordinates(
              storeInfo.location.coordinates
            );
            return normalizedCoords
              ? {
                  ...storeInfo.location,
                  coordinates: normalizedCoords,
                }
              : storeInfo.location;
          })()
        : undefined,
    phone: normalizePhoneNumber(storeInfo.phone),
  };
  const standardizedMerchant: UpdateGfMerchantDto = extractMerchantData(
    storeInfo,
    placeDetails
  );

  return {
    store: standardizedStore,
    merchant: standardizedMerchant,
    metadata: {
      isFromGoogleApi: true,
      hasGooglePlaceDetails: !!placeDetails,
      googlePlaceId: placeDetails?.place_id,
      originalGoogleData: placeDetails,
    },
  };
};

/**
 * Normalize opening hours to consistent OpeningHour[] format
 */
const normalizeOpeningHours = (hours: any): OpeningHourDto[] => {
  if (!hours) return [];

  // If it's already an array of OpeningHour objects, validate and return
  if (
    Array.isArray(hours) &&
    hours.every((h) => h && typeof h === "object" && h.open && h.close)
  ) {
    return hours.filter(Boolean).map((hour) => ({
      open: {
        day: hour.open?.day || 0,
        time: hour.open?.time || "0900",
      },
      close: {
        day: hour.close?.day || 0,
        time: hour.close?.time || "1700",
      },
    }));
  }

  // If it's an array of strings, convert to OpeningHour format
  if (Array.isArray(hours) && hours.every((h) => typeof h === "string")) {
    return hours.map((hourStr, index) => {
      const parsed = parseHourString(hourStr);
      return {
        open: {
          day: index, // Use array index as day (0-6)
          time: parsed.openTime,
        },
        close: {
          day: index,
          time: parsed.closeTime,
        },
      };
    });
  }

  // If it's a mixed array, handle each item appropriately
  if (Array.isArray(hours)) {
    return hours.map((hour, index) => {
      if (!hour) {
        return {
          open: { day: index, time: "0900" },
          close: { day: index, time: "1700" },
        };
      }

      if (typeof hour === "string") {
        const parsed = parseHourString(hour);
        return {
          open: { day: index, time: parsed.openTime },
          close: { day: index, time: parsed.closeTime },
        };
      }

      if (typeof hour === "object" && hour.open && hour.close) {
        return {
          open: {
            day: hour.open.day || index,
            time: hour.open.time || "0900",
          },
          close: {
            day: hour.close.day || index,
            time: hour.close.time || "1700",
          },
        };
      }

      // Default fallback
      return {
        open: { day: index, time: "0900" },
        close: { day: index, time: "1700" },
      };
    });
  }

  // If it's a single string, convert to 7-day format
  if (typeof hours === "string") {
    const parsed = parseHourString(hours);
    return Array.from({ length: 7 }, (_, index) => ({
      open: { day: index, time: parsed.openTime },
      close: { day: index, time: parsed.closeTime },
    }));
  }

  // Default: 7 days with standard hours
  return Array.from({ length: 7 }, (_, index) => ({
    open: { day: index, time: "0900" },
    close: { day: index, time: "1700" },
  }));
};

/**
 * Parse a hour string to extract open and close times
 */
const parseHourString = (
  hourStr: string
): { openTime: string; closeTime: string } => {
  if (!hourStr || typeof hourStr !== "string") {
    return { openTime: "0900", closeTime: "1700" };
  }

  // Handle "HHMM-HHMM" format
  if (/^\d{4}-\d{4}$/.test(hourStr)) {
    const [openTime, closeTime] = hourStr.split("-");
    return { openTime, closeTime };
  }

  // Handle "HH:MM-HH:MM" format
  if (/^\d{2}:\d{2}-\d{2}:\d{2}$/.test(hourStr)) {
    const [openTime, closeTime] = hourStr
      .split("-")
      .map((time) => time.replace(":", ""));
    return { openTime, closeTime };
  }

  // Handle other formats and convert to HHMM-HHMM
  // This is a simplified version - expand based on actual data formats
  return { openTime: "0900", closeTime: "1700" };
};

/**
 * Normalize coordinates to [longitude, latitude] format
 */
const normalizeCoordinates = (coords: any): number[] | undefined => {
  if (!coords || !Array.isArray(coords)) return undefined;

  const [lng, lat] = coords;

  if (
    typeof lng === "number" &&
    typeof lat === "number" &&
    isFinite(lng) &&
    isFinite(lat)
  ) {
    return [lng, lat];
  }

  return undefined;
};

/**
 * Normalize phone number format
 */
const normalizePhoneNumber = (phone: any): string => {
  if (!phone || typeof phone !== "string") return "";

  // Remove any non-digit characters except + at the beginning
  const cleaned = phone.replace(/[^\d+]/g, "");

  // Ensure + is only at the beginning
  if (cleaned.startsWith("+")) {
    return "+" + cleaned.substring(1).replace(/\+/g, "");
  }

  return cleaned;
};

/**
 * Extract domain from URL
 */
const extractDomainFromUrl = (url: string): string => {
  if (!url || typeof url !== "string") return "";

  try {
    const urlObj = new URL(url);
    return urlObj.hostname.replace("www.", "");
  } catch {
    const match = url.match(/(?:https?:\/\/)?(?:www\.)?([^/]+)/);
    return match ? match[1] : "";
  }
};

/**
 * Extract merchant data from store data and optional Google Place details
 */
const extractMerchantData = (
  storeData: UpdateGfStoreDto,
  placeDetails?: any
): UpdateGfMerchantDto => {
  // If we have Google Place details, extract merchant info from there
  if (placeDetails) {
    const domain = extractDomainFromUrl(placeDetails.website || "");

    const result = {
      name: placeDetails.name || storeData.signature || "",
      nameChinese: storeData.merchant?.nameChinese || "",
      domain: domain,
      merchantLogo: storeData.merchant?.merchantLogo,
      merchantMapSmallLogo: storeData.merchant?.merchantMapSmallLogo,
    };

    return result;
  }

  // Fallback to existing logic for non-Google API data
  const fallbackResult = {
    name: storeData.merchant?.name || storeData.signature || "",
    nameChinese: storeData.merchant?.nameChinese || "",
    domain: storeData.merchant?.domain || "",
    merchantLogo: storeData.merchant?.merchantLogo,
    merchantMapSmallLogo: storeData.merchant?.merchantMapSmallLogo,
  };

  return fallbackResult;
};

/**
 * Validate standardized data before saving
 */
export const validateOnboardingData = (
  data: StandardizedOnboardingData
): {
  isValid: boolean;
  errors: string[];
} => {
  const errors: string[] = [];

  // Validate store data
  if (!data.store.location?.coordinates?.[0]) {
    errors.push("Store longitude is required");
  }
  if (!data.store.location?.coordinates?.[1]) {
    errors.push("Store latitude is required");
  }
  if (!data.store.phone) {
    errors.push("Store phone number is required");
  }
  if (!data.store.openingHours || data.store.openingHours.length === 0) {
    errors.push("Store openingHours is required");
  }

  // Validate merchant data
  if (!data.merchant.name) {
    errors.push("Store name is required");
  }

  if (!data.merchant.nameChinese) {
    errors.push("Store name chinese is required");
  }

  if (!data.merchant.domain) {
    errors.push("Store domain is required");
  }
  if (!data.merchant.merchantLogo) {
    errors.push("Store logo is required");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Get merchant data for saving
 */
export const getMerchantForSaving = (
  data: StandardizedOnboardingData
): UpdateGfMerchantDto => {
  return data.merchant;
};

/**
 * Get store data for saving (with merchant reference)
 */
export const getStoreForSaving = (
  data: StandardizedOnboardingData,
  merchantId?: string
): UpdateGfStoreDto => {
  const storeData = { ...data.store };

  // If merchant ID is provided, update the merchant reference
  if (merchantId && storeData.merchant) {
    storeData.merchant = {
      ...storeData.merchant,
      id: merchantId,
    };
  }

  return storeData;
};

/**
 * Convert time from HHMM format to number format for API
 * Can return decimal hours (e.g., "0830" -> 8.5) or integer hours (e.g., "2100" -> 21)
 */
const timeStringToNumber = (timeStr: string): number => {
  if (!timeStr || timeStr.length !== 4) return 0;

  const hours = parseInt(timeStr.substring(0, 2), 10);
  const minutes = parseInt(timeStr.substring(2, 4), 10);

  // Return decimal hours if there are minutes, otherwise integer hours
  if (minutes === 0) {
    return hours;
  }

  return hours + minutes / 60;
};

/**
 * Convert time from number format to HHMM string format
 * Handles both integer hours (e.g., 21 -> "2100") and decimal hours (e.g., 8.5 -> "0830")
 */
const timeNumberToString = (timeNum: number): string => {
  if (!timeNum || timeNum <= 0) return "0000";

  // Handle decimal hours (e.g., 8.5 = 8:30)
  if (timeNum % 1 !== 0) {
    const hours = Math.floor(timeNum);
    const minutes = Math.round((timeNum - hours) * 60);
    const hoursStr = hours.toString().padStart(2, "0");
    const minutesStr = minutes.toString().padStart(2, "0");
    return hoursStr + minutesStr;
  }

  // Handle integer hours (e.g., 21 = 21:00)
  if (timeNum < 100) {
    // Single or double digit hours, add "00" for minutes
    return timeNum.toString().padStart(2, "0") + "00";
  }

  // Handle HHMM format numbers (e.g., 1530 = 15:30)
  return timeNum.toString().padStart(4, "0");
};

/**
 * Extract individual time fields from openingHours array
 * Senior developer approach: Handles edge cases, validates data, and provides fallbacks
 */
export const extractIndividualTimesFromOpeningHours = (
  openingHours: OpeningHourDto[]
): {
  openTime: number;
  closeTime: number;
  saturdayOpenTime: number;
  saturdayCloseTime: number;
  sundayOpenTime: number;
  sundayClosedTime: number;
} => {
  const defaultTimes = {
    openTime: 9, // 9:00 AM
    closeTime: 17, // 5:00 PM
    saturdayOpenTime: 9,
    saturdayCloseTime: 17,
    sundayOpenTime: 9,
    sundayClosedTime: 17,
  };

  if (!Array.isArray(openingHours) || openingHours.length === 0) {
    return defaultTimes;
  }
  try {
    const sundayHours = openingHours.find((h) => h.open?.day === 0);
    const saturdayHours = openingHours.find((h) => h.open?.day === 6);
    const weekdayHoursList = openingHours.filter(
      (h) => h.open?.day !== undefined && h.open.day >= 1 && h.open.day <= 5
    );

    let weekdayHours = null;
    if (weekdayHoursList.length > 0) {
      weekdayHours = weekdayHoursList.find((h) => h.open?.day === 1); // Monday

      if (!weekdayHours) {
        const openTimeCounts: Record<string, number> = {};
        const closeTimeCounts: Record<string, number> = {};

        weekdayHoursList.forEach((h) => {
          const openTime = h.open?.time || "0900";
          const closeTime = h.close?.time || "1700";
          openTimeCounts[openTime] = (openTimeCounts[openTime] || 0) + 1;
          closeTimeCounts[closeTime] = (closeTimeCounts[closeTime] || 0) + 1;
        });

        const mostCommonOpenTime = Object.entries(openTimeCounts).reduce(
          (a, b) => (openTimeCounts[a[0]] > openTimeCounts[b[0]] ? a : b)
        )[0];

        const mostCommonCloseTime = Object.entries(closeTimeCounts).reduce(
          (a, b) => (closeTimeCounts[a[0]] > closeTimeCounts[b[0]] ? a : b)
        )[0];

        weekdayHours =
          weekdayHoursList.find(
            (h) =>
              h.open?.time === mostCommonOpenTime &&
              h.close?.time === mostCommonCloseTime
          ) ||
          weekdayHoursList.find((h) => h.open?.time === mostCommonOpenTime) ||
          weekdayHoursList[0];
      }
    }

    if (!weekdayHours) {
      weekdayHours = openingHours[0];
    }

    return {
      openTime: weekdayHours?.open?.time
        ? timeStringToNumber(weekdayHours.open.time)
        : defaultTimes.openTime,
      closeTime: weekdayHours?.close?.time
        ? timeStringToNumber(weekdayHours.close.time)
        : defaultTimes.closeTime,
      saturdayOpenTime: saturdayHours?.open?.time
        ? timeStringToNumber(saturdayHours.open.time)
        : defaultTimes.saturdayOpenTime,
      saturdayCloseTime: saturdayHours?.close?.time
        ? timeStringToNumber(saturdayHours.close.time)
        : defaultTimes.saturdayCloseTime,
      sundayOpenTime: sundayHours?.open?.time
        ? timeStringToNumber(sundayHours.open.time)
        : defaultTimes.sundayOpenTime,
      sundayClosedTime: sundayHours?.close?.time
        ? timeStringToNumber(sundayHours.close.time)
        : defaultTimes.sundayClosedTime,
    };
  } catch (error) {
    console.warn(
      "Error extracting individual times from opening hours:",
      error
    );
    return defaultTimes;
  }
};

/**
 * Generate openingHours array from individual time fields
 * Senior developer approach: Ensures data consistency and handles edge cases
 * Supports both decimal hours (8.5 = 8:30) and integer hours (21 = 21:00)
 */
export const generateOpeningHoursFromIndividualTimes = (
  openTime: number,
  closeTime: number,
  saturdayOpenTime: number,
  saturdayCloseTime: number,
  sundayOpenTime: number,
  sundayClosedTime: number
): OpeningHourDto[] => {
  try {
    // Validate inputs and provide fallbacks (default to 9:00 AM - 5:00 PM)
    const safeOpenTime = timeNumberToString(openTime || 9);
    const safeCloseTime = timeNumberToString(closeTime || 17);
    const safeSaturdayOpenTime = timeNumberToString(saturdayOpenTime || 9);
    const safeSaturdayCloseTime = timeNumberToString(saturdayCloseTime || 17);
    const safeSundayOpenTime = timeNumberToString(sundayOpenTime || 9);
    const safeSundayClosedTime = timeNumberToString(sundayClosedTime || 17);

    // Generate 7 days of opening hours (0=Sunday, 1=Monday, ..., 6=Saturday)
    const openingHours: OpeningHourDto[] = [];

    // Sunday (day 0)
    openingHours.push({
      open: { day: 0, time: safeSundayOpenTime },
      close: { day: 0, time: safeSundayClosedTime },
    });

    // Monday to Friday (days 1-5) - use weekday hours
    for (let day = 1; day <= 5; day++) {
      openingHours.push({
        open: { day, time: safeOpenTime },
        close: { day, time: safeCloseTime },
      });
    }

    // Saturday (day 6)
    openingHours.push({
      open: { day: 6, time: safeSaturdayOpenTime },
      close: { day: 6, time: safeSaturdayCloseTime },
    });

    return openingHours;
  } catch (error) {
    console.warn(
      "Error generating opening hours from individual times:",
      error
    );
    // Return default 7-day schedule
    return Array.from({ length: 7 }, (_, index) => ({
      open: { day: index, time: "0900" },
      close: { day: index, time: "1700" },
    }));
  }
};

/**
 * Synchronize both openingHours and individual time fields in store data
 * Senior developer approach: Ensures data consistency across different formats
 */
export const syncOpeningHoursData = (
  storeData: UpdateGfStoreDto,
  prioritizeFormat: "openingHours" | "individualTimes" = "openingHours"
): UpdateGfStoreDto => {
  try {
    if (prioritizeFormat === "openingHours" && storeData.openingHours?.length) {
      // Extract individual times from openingHours
      const individualTimes = extractIndividualTimesFromOpeningHours(
        storeData.openingHours
      );

      return {
        ...storeData,
        openTime: individualTimes.openTime,
        closeTime: individualTimes.closeTime,
        saturdayOpenTime: individualTimes.saturdayOpenTime,
        saturdayCloseTime: individualTimes.saturdayCloseTime,
        sundayOpenTime: individualTimes.sundayOpenTime,
        sundayClosedTime: individualTimes.sundayClosedTime,
      };
    } else if (storeData.openTime || storeData.closeTime) {
      // Generate openingHours from individual times
      const openingHours = generateOpeningHoursFromIndividualTimes(
        storeData.openTime || 9,
        storeData.closeTime || 17,
        storeData.saturdayOpenTime || 9,
        storeData.saturdayCloseTime || 17,
        storeData.sundayOpenTime || 9,
        storeData.sundayClosedTime || 17
      );

      return {
        ...storeData,
        openingHours,
      };
    }

    return storeData;
  } catch (error) {
    console.warn("Error syncing opening hours data:", error);
    return storeData;
  }
};
